import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbMessage } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { type LoadChatPayload, parseLoadChatPayload } from "./loadChatSchema";

interface LoadChatRendererProps {
  block: StreamingBlock;
}

export const LoadChatRenderer = (props: LoadChatRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LoadChatPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLoadChatPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  // Link semantics come from the host: bluesky supplies hrefs + router
  // navigation; a surface without either (chief) renders a plain row.
  const href = (chatId: string): string | undefined => rt.actions.chatCitationHref?.({ chatId, messageId: "" });

  // Left-click navigates within the SPA; modified clicks fall through to the
  // browser so middle-click / cmd-click still open a new tab from the href.
  const openChat = (e: MouseEvent, chatId: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (!rt.actions.navigateToChat) return;
    e.preventDefault();
    rt.actions.navigateToChat({ chatId, messageId: "" });
  };

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("load_chat_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <Show
            when={!data().error}
            fallback={
              <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-sm text-muted-text">
                {data().error}
              </div>
            }
          >
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <Show
                when={href(data().chatId) || rt.actions.navigateToChat}
                fallback={
                  <div class="flex items-center gap-2 px-3 py-2">
                    <TbMessage class="size-4 shrink-0 text-brand" />
                    <span class="min-w-0 truncate text-sm font-medium text-primary-text">
                      {data().chatTitle || rt.t("load_chat_card.untitled")}
                    </span>
                  </div>
                }
              >
                <a
                  href={href(data().chatId) ?? "#"}
                  onClick={(e) => openChat(e, data().chatId)}
                  class="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-panel-hover"
                >
                  <TbMessage class="size-4 shrink-0 text-brand" />
                  <span class="min-w-0 truncate text-sm font-medium text-primary-text">
                    {data().chatTitle || rt.t("load_chat_card.untitled")}
                  </span>
                </a>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
